import clsx from 'clsx';
import styles from './MyHomes.module.css';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { managerDetailsSchema } from '../../utils/addPropertyScehmas/managerDetailsSchema';
import { Button, Tooltip, Whisper } from 'rsuite';
import { ArrowRight } from 'lucide-react';
import infoIcon from '../../../assets/pages/addProperty/rentalInfo/infoIcon.svg';
import { getBuildingDetailsById, updateBuilding } from '../../../auth/_requests';
import { useAuth } from '../../../auth/Auth';
import { useModal } from '../../hooks/modalContext';
import { showToast } from '../../utils/showToast';
import { buildingDetails } from '../../utils/types';
import {
  DEFAULT_SECURITY_QUESTION,
  INTERAC_SECURITY_QUESTIONS,
  OTHER_SECURITY_QUESTION_OPTION,
  resolveSecurityQuestionOption,
} from '../../utils/interacSecurityQuestions';

const getInitialValues = () => ({
  fullName: '',
  email: '',
  question: DEFAULT_SECURITY_QUESTION,
  answer: '',
});

interface props {
  isModal?: boolean;
  tenantBuildingId: string;
  buildingItem?: buildingDetails;
  getBuildingIsActive?: (buildingItem: buildingDetails) => boolean;
  ensureBuildingId?: (rentAmount?: number | null) => Promise<string>;
}

const PropertyManagerData = ({
  isModal,
  tenantBuildingId,
  buildingItem,
  getBuildingIsActive,
  ensureBuildingId,
}: props) => {
  const isBuildingActive =
    buildingItem && getBuildingIsActive ? getBuildingIsActive(buildingItem) : true;
  const { auth } = useAuth();
  const [loadingBtn, setLoadingBtn] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const { setModal, modalState } = useModal();
  const payRentSetupFlow = modalState?.payRentSetupFlow === true;
  const isNewBuilding = modalState?.isNewBuilding === true;
  const isFullAddHomeFlow = payRentSetupFlow || isNewBuilding;
  const [questionOption, setQuestionOption] = useState(DEFAULT_SECURITY_QUESTION);
  const savedPayNumber = buildingItem?.building?.payNumber;
  const switchingToExternal = !isModal && !!savedPayNumber;

  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema: managerDetailsSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      if (loadingBtn) return;
      try {
        setLoadingBtn(true);
        const buildingId = ensureBuildingId ? await ensureBuildingId() : tenantBuildingId;

        if (!buildingId) {
          throw new Error('Building id is required');
        }

        const requestBody = {
          managerDetails: {
            email: values.email,
            firstName: values.fullName,
            lastName: '',
            isIndividual: true,
            securityQuestion: values.question,
            securityAnswer: values.answer,
            phoneNumber: '0000000000',
            phoneNumberExtension: '1',
          },
        };

        // Switching from a TenantPay account number to landlord details removes
        // the payNumber, so the building only ever has one of the two.
        await updateBuilding(
          auth?.data.token,
          buildingId,
          switchingToExternal
            ? { ...requestBody, buildingDetails: { payNumber: null } }
            : requestBody
        );

        if (isModal) {
          showToast({ message: 'Manager details saved successfully.', type: 'success' });
          setTimeout(() => {
            if (isFullAddHomeFlow) {
              setModal('add-home', {
                payRentSetupFlow,
                isNewBuilding,
                refreshProfile: true,
                refreshProperties: true,
                tenantBuildingId: buildingId,
              });
            } else {
              setModal('add-building-address', {
                refreshProfile: true,
                tenantBuildingId: buildingId,
              });
            }
            setLoadingBtn(false);
          }, 1000);
        } else {
          showToast({ message: 'Manager details updated successfully.', type: 'success' });
          setTimeout(() => {
            setModal(null, { refreshProperties: true });
            setLoadingBtn(false);
          }, 1000);
        }
      } catch (error) {
        console.error('Error updating manager details:', error);
        showToast({
          message: 'Error updating manager details. Please try again.',
          type: 'error',
        });
        setLoadingBtn(false);
      }
    },
  });

  useEffect(() => {
    if (!tenantBuildingId) {
      return;
    }

    const fetchBuildingDetails = async () => {
      try {
        setFetchingData(true);
        const response = await getBuildingDetailsById(auth?.data.token, tenantBuildingId);
        const data = response.data[0];

        if (data?.tenantManager) {
          const manager = data.tenantManager;
          const question = manager.securityQuestion || DEFAULT_SECURITY_QUESTION;
          setQuestionOption(resolveSecurityQuestionOption(question));
          formik.setValues({
            fullName: manager.firstName || '',
            email: manager.email || '',
            question,
            answer: manager.securityAnswer || '',
          });
        }
      } catch (error) {
        console.error('Error fetching details of a building');
      } finally {
        setFetchingData(false);
      }
    };

    fetchBuildingDetails();
  }, [auth?.data.token, tenantBuildingId]);

  const handleQuestionOptionChange = (value: string) => {
    setQuestionOption(value);
    if (value === OTHER_SECURITY_QUESTION_OPTION) {
      formik.setFieldValue('question', '');
      formik.setFieldTouched('question', false);
      return;
    }

    formik.setFieldValue('question', value);
  };

  const canSubmit = formik.isValid && !loadingBtn && !fetchingData;

  return (
    <div className={clsx(isModal && styles.modal, isModal && styles.modalContainer)}>
      <form
        id='property-manager-details-form'
        className={clsx(styles.detailsOuter, isModal && styles.modalForm)}
        onSubmit={formik.handleSubmit}
      >
        <div className={isModal ? styles.modalScrollContent : undefined}>
          {switchingToExternal && (
            <div className='mb-4 rounded-[10px] border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-800'>
              You&apos;re switching to landlord details. Your TenantPay account number (
              {savedPayNumber}) will be removed when you save.
            </div>
          )}
          <div className={styles.fieldOuter}>
            <label className='label'>
              Landlord or property manager name<span className={styles.imp}>*</span>
            </label>
            {fetchingData ? (
              <div className='inputFieldLoading' />
            ) : (
              <input
                type='text'
                autoComplete='off'
                {...formik.getFieldProps('fullName')}
                placeholder='e.g. Jane Smith or ABC Property Management'
                disabled={!isBuildingActive}
                className={clsx(
                  formik.touched.fullName && formik.errors.fullName ? styles.isInvalid : '',
                  formik.touched.fullName && !formik.errors.fullName ? styles.valid : '',
                  `inputField `
                )}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                  }
                }}
              />
            )}
            {formik.touched.fullName && formik.errors.fullName && (
              <div className={styles.error}>
                <span>{formik.errors.fullName}</span>
              </div>
            )}
            <div className={styles.tenantSubhead}>
              The person or company you pay rent to. A best guess is fine, we use it for compliance.
            </div>
          </div>
          <div className={styles.fieldFlex}>
            <div
              className={`${styles.fieldOuter} inputWrapper ${fetchingData && 'justify-between'}`}
            >
              <label className='label'>
                {isModal ? `Landlord or property manager email` : `Landlord’s Email`}
                <span className={styles.imp}>*</span>
              </label>
              {fetchingData ? (
                <div className='inputFieldLoading' />
              ) : (
                <input
                  type='email'
                  autoComplete='off'
                  {...formik.getFieldProps('email')}
                  placeholder='name@example.com'
                  disabled={!isBuildingActive}
                  className={clsx(
                    formik.touched.email && formik.errors.email ? styles.isInvalid : '',
                    formik.touched.email && !formik.errors.email ? styles.valid : '',
                    `inputField `
                  )}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                    }
                  }}
                />
              )}
              {formik.touched.email && formik.errors.email && (
                <div className={styles.error}>
                  <span>{formik.errors.email}</span>
                </div>
              )}
              {isModal && (
                <div className={styles.tenantSubhead}>
                  Where rent payment notices and Interac transfers can be sent.
                </div>
              )}
            </div>
          </div>
          <div
            className={clsx(styles.fieldOuter, 'inputWrapper', 'ph-no-capture')}
            data-clarity-mask='true'
          >
            <div className='flex items-center justify-between '>
              <label className='label'>
                Interac security question<span className={styles.imp}>*</span>
              </label>
              <div className={styles.infoOuter}>
                <Whisper
                  trigger='hover'
                  placement={'top'}
                  controlId={`control-id-top`}
                  speaker={
                    <Tooltip>
                      Used when sending rent via Interac e-Transfer to your landlord or property
                      manager. Share the answer with them so they can deposit the payment.
                    </Tooltip>
                  }
                >
                  <Button appearance='subtle' className={styles.btnImg}>
                    <img src={infoIcon} className={styles.infoIcon} alt='info' />
                  </Button>
                </Whisper>
              </div>
            </div>
            {fetchingData ? (
              <div className='inputFieldLoading' />
            ) : (
              <select
                className={clsx(
                  styles.selectField,
                  'inputField',
                  formik.touched.question &&
                    formik.errors.question &&
                    questionOption !== OTHER_SECURITY_QUESTION_OPTION
                    ? styles.isInvalid
                    : '',
                  formik.touched.question &&
                    !formik.errors.question &&
                    questionOption !== OTHER_SECURITY_QUESTION_OPTION
                    ? styles.valid
                    : ''
                )}
                value={questionOption}
                disabled={!isBuildingActive}
                onChange={(event) => handleQuestionOptionChange(event.target.value)}
              >
                {INTERAC_SECURITY_QUESTIONS.map((question) => (
                  <option key={question} value={question}>
                    {question}
                  </option>
                ))}
              </select>
            )}
            {questionOption === OTHER_SECURITY_QUESTION_OPTION && (
              <>
                {fetchingData ? (
                  <div className='inputFieldLoading' />
                ) : (
                  <input
                    type='text'
                    autoComplete='off'
                    {...formik.getFieldProps('question')}
                    placeholder='Type your own security question'
                    disabled={!isBuildingActive}
                    className={clsx(
                      styles.tenantInput,
                      formik.touched.question && formik.errors.question ? styles.isInvalid : '',
                      formik.touched.question && !formik.errors.question ? styles.valid : '',
                      'inputField'
                    )}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                      }
                    }}
                  />
                )}
              </>
            )}
            {formik.touched.question && formik.errors.question && (
              <div className={styles.error}>
                <span>{formik.errors.question}</span>
              </div>
            )}
            <div className={styles.tenantSubhead}>
              {questionOption === OTHER_SECURITY_QUESTION_OPTION
                ? '5–40 characters. Spaces allowed (not at start/end). Use letters, numbers, and .?!, only.'
                : 'Choose a question your landlord can answer, or select Other to write your own.'}
            </div>
          </div>
          <div
            className={clsx(styles.fieldOuter, 'inputWrapper', 'ph-no-capture')}
            data-clarity-mask='true'
          >
            <label className='label'>
              Security answer<span className={styles.imp}>*</span>
            </label>
            {fetchingData ? (
              <div className='inputFieldLoading' />
            ) : (
              <input
                type='text'
                autoComplete='off'
                {...formik.getFieldProps('answer')}
                placeholder='Enter the answer to the security question'
                disabled={!isBuildingActive}
                className={clsx(
                  formik.touched.answer && formik.errors.answer ? styles.isInvalid : '',
                  formik.touched.answer && !formik.errors.answer ? styles.valid : '',
                  'inputField'
                )}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                  }
                }}
              />
            )}

            {formik.touched.answer && formik.errors.answer && (
              <div className={styles.error}>
                <span>{formik.errors.answer}</span>
              </div>
            )}
            <div className={styles.tenantSubhead}>
              1–25 characters. Letters and numbers only. Share with your landlord.
            </div>
          </div>
        </div>
        {!isModal ? (
          <div className={styles.buttonOuter}>
            <button
              type='button'
              className={`${styles.btn1} cancelBtn`}
              onClick={() => setModal(null)}
            >
              Cancel
            </button>

            <button
              type='submit'
              className={`${styles.btn2} submitBtn`}
              disabled={!isBuildingActive || loadingBtn}
            >
              {loadingBtn ? 'Saving...' : 'Save'}
            </button>
          </div>
        ) : null}
      </form>
      {isModal ? (
        <div className={styles.modalFooter}>
          <button
            className={clsx(styles.nextBtn, canSubmit && styles.submitBtn)}
            disabled={!canSubmit}
            form='property-manager-details-form'
            type='submit'
          >
            <span>{loadingBtn ? 'Please wait' : 'Next'}</span>
            <ArrowRight stroke={!canSubmit ? '#0E0E0B66' : '#fff'} />
          </button>
        </div>
      ) : null}
    </div>
  );
};
export default PropertyManagerData;
